/**
 * Koernung und Bindigkeit hinter einem Bodenart-Kuerzel - das, was das
 * Grubenbild braucht, um Sand anders zu zeichnen als Lehm und Grobsand
 * anders als Feinsand.
 *
 * `korn` 0 (Staubsand) bis 5 (Grobsand) steuert, wie grob die Koernung
 * gezeichnet wird; `bindig` 0 (reiner Sand) bis 6 (Ton) steuert, ob Koerner
 * oder ein bindiges Gefuege gezeichnet werden.
 *
 * ⚠ Klartexte stehen nur dort, wo sie belegt sind: Nebenkoernungsarten aus
 * SEA 95 Teil A, Darst. A 1-6 (S. 18), Koernungsarten aus Darst. A 1-5
 * (S. 17, Fingerprobe), Vorsilben aus Abschnitt 1.1.1.3 (S. 17: S rein,
 * alS anlehmig, lS lehmig). Wo die Anweisung ein Kuerzel nicht nennt, steht
 * KEIN erfundener Klartext, sondern `undefined` - dann zeigt das Bild nur
 * das Kuerzel.
 */

export type Substratgruppe = 'sand' | 'bindig' | 'organisch';

export interface BodenartBeschreibung {
	gruppe: Substratgruppe;
	korn: number;
	bindig: number;
	text?: string;
}

export const BODENARTEN: Readonly<Record<string, BodenartBeschreibung>> = {
	// reine Sande
	'gS': { gruppe: 'sand', korn: 5, bindig: 0, text: 'Grobsand' },
	'mS': { gruppe: 'sand', korn: 3, bindig: 0, text: 'Mittelsand' },
	'mfS': { gruppe: 'sand', korn: 2, bindig: 0, text: 'Mittelfeinsand' },
	'fS': { gruppe: 'sand', korn: 1, bindig: 0, text: 'Feinsand' },
	'stbS': { gruppe: 'sand', korn: 0, bindig: 0, text: 'Staubsand' },
	// anlehmige Sande
	'algS': { gruppe: 'sand', korn: 5, bindig: 1, text: 'anlehmiger Grobsand' },
	'almS': { gruppe: 'sand', korn: 3, bindig: 1, text: 'anlehmiger Mittelsand' },
	'almfS': { gruppe: 'sand', korn: 2, bindig: 1, text: 'anlehmiger Mittelfeinsand' },
	'alfS': { gruppe: 'sand', korn: 1, bindig: 1, text: 'anlehmiger Feinsand' },
	'alstbS': { gruppe: 'sand', korn: 0, bindig: 1, text: 'anlehmiger Staubsand' },
	// lehmige Sande
	'lgS': { gruppe: 'sand', korn: 5, bindig: 2, text: 'lehmiger Grobsand' },
	'lmS': { gruppe: 'sand', korn: 3, bindig: 2, text: 'lehmiger Mittelsand' },
	'lmfS': { gruppe: 'sand', korn: 2, bindig: 2, text: 'lehmiger Mittelfeinsand' },
	'lfS': { gruppe: 'sand', korn: 1, bindig: 2, text: 'lehmiger Feinsand' },
	'lstbS': { gruppe: 'sand', korn: 0, bindig: 2, text: 'lehmiger Staubsand' },
	// Die Vorsilbe „u" nennt die Anweisung an dieser Stelle nicht; darum ohne
	// Klartext, nur als feiner Sand gezeichnet.
	'ufS': { gruppe: 'sand', korn: 1, bindig: 2 },
	// bindige Koernungsarten
	'sL': { gruppe: 'bindig', korn: 1, bindig: 3, text: 'sandiger Lehm' },
	'L': { gruppe: 'bindig', korn: 0, bindig: 4, text: 'Lehm' },
	'stL': { gruppe: 'bindig', korn: 0, bindig: 4 },
	'lU': { gruppe: 'bindig', korn: 0, bindig: 4, text: 'lehmiger Schluff' },
	'UL': { gruppe: 'bindig', korn: 0, bindig: 4, text: 'Schlufflehm' },
	'U': { gruppe: 'bindig', korn: 0, bindig: 4 },
	'tU': { gruppe: 'bindig', korn: 0, bindig: 5 },
	'sU': { gruppe: 'bindig', korn: 1, bindig: 4 },
	'uT': { gruppe: 'bindig', korn: 0, bindig: 5 },
	'lT': { gruppe: 'bindig', korn: 0, bindig: 5, text: 'lehmiger Ton' },
	'T': { gruppe: 'bindig', korn: 0, bindig: 6, text: 'Ton' },
	'sT': { gruppe: 'bindig', korn: 1, bindig: 5 },
	'K': { gruppe: 'bindig', korn: 0, bindig: 4 },
};

/**
 * Organische Substrate (Torf- und Muddearten) werden hier absichtlich NICHT
 * einzeln gedeutet: Ihre Kuerzel stehen im Torfarten-Katalog der App, und ein
 * geratener Klartext waere schlimmer als keiner. Fuers Bild genuegt, dass es
 * organisch ist - gezeichnet wird eine Faserung.
 */
export function bodenart(kuerzel: string | undefined | null): BodenartBeschreibung | null {
	if (!kuerzel) return null;
	return BODENARTEN[kuerzel] ?? { gruppe: 'organisch', korn: 0, bindig: 0 };
}

/** Was im Bild neben der Schicht steht: Kuerzel, und der Klartext nur wenn belegt. */
export function bodenartText(kuerzel: string | undefined | null): string {
	if (!kuerzel) return '';
	const art = BODENARTEN[kuerzel];
	if (art?.text) return `${kuerzel}: ${art.text}`;
	if (!art) return `${kuerzel}: organisches Substrat`;
	return kuerzel;
}

/**
 * Mittlerer Volumenanteil je Skelettstufe in Prozent (Klassenmitte der Spannen
 * aus SEA 95 Teil B). Er steuert, wie viel Flaeche die Kiesel im Bild belegen -
 * „sehr schwach steinig" sind rund 1 %, und so viel soll auch zu sehen sein.
 */
export const SKELETT_ANTEIL: readonly number[] = [0, 1.05, 3.5, 7.5, 17.5, 37.5, 62.5, 87.5];
